import { useCustomToast } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useMutationWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import type { BulkUpsertRow } from 'pages/exclusive-deals/components/editable-deals-grid/editable-deal-row.types';
import type { UseMutationOptions, UseMutationResult } from 'react-query';
import { useGetExclusiveDealsApi } from './use-get-exclusive-deals-api';

const endpoint = 'ums/v1/cms/exclusive-deals/bulk';
export const bulkUpsertDealsEndpointIdentifier = endpoint;

export interface BulkUpsertResultDto {
  created: number;
  updated: number;
}

export interface BulkUpsertErrorDto {
  statusCode?: number;
  message: string;
  errors?: string[];
}

type TData = { data: BulkUpsertResultDto };
type TError = AxiosError<BulkUpsertErrorDto>;
type TVariables = { rows: BulkUpsertRow[] };
type Options = Omit<UseMutationOptions<TData, TError, TVariables, unknown>, 'mutationFn'>;
type ReturnType = UseMutationResult<TData, TError, TVariables>;

/**
 * Bulk create/update exclusive deals via POST /ums/v1/cms/exclusive-deals/bulk.
 * Rows with an `id` update that deal; rows without one create new deals.
 * All-or-nothing: a 400 carries per-row messages in `errors` and nothing is written.
 */
export function useBulkUpsertDealsApi(options?: Options): ReturnType {
  const { refetch: refetchDeals } = useGetExclusiveDealsApi({ enabled: false });
  const toast = useCustomToast();

  const mutationFn = async ({ rows }: TVariables): Promise<TData> => {
    try {
      const { data } = await http.post(endpoint, { rows });
      return data;
    } catch (error: any) {
      if (!error.response?.status || !error.response?.data?.message) throw error;
      // Per-row errors are rendered in the grid; show the summary message as a toast.
      toast({ description: error.response.data.message, status: 'error' });
      throw error;
    }
  };

  return useMutationWrapper(mutationFn, {
    mutationKey: [endpoint],
    onSuccess: (data: TData) => {
      refetchDeals();
      const { created, updated } = data.data;
      toast({ description: `Saved: ${created} created, ${updated} updated`, status: 'success' });
    },
    ...options,
  });
}
